import {
  ArrowPathIcon,
  BellIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import {
  Eye,
  FileCheck,
  PenLine,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  HipaaESignRequestModal,
  HipaaESignStatusBadge,
  HipaaReminderModal,
  HipaaVoidConfirmDialog,
  useHipaaESignStatus,
} from '@/components/hipaa-esign';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import {
  useHipaaAuthorizationCreateOrUpdateMutation,
  useHipaaAuthorizationDestroyMutation,
  useHipaaAuthorizationRetrieveQuery,
} from '@/services/api/hipaaAuthorizationService';
import { useGetMatterQuery } from '@/services/api/mattersService';

import { SignatureUploadModal } from './SignatureUploadModal';

interface HipaaSignatureFieldProps {
  clientId: string;
  isEditing: boolean;
  matterId?: string;
  /** Client name for e-sign request */
  clientName?: string;
  /** Client email for e-sign request */
  clientEmail?: string | null;
  /** Client phone for e-sign request */
  clientPhone?: string | null;
}

export const HipaaSignatureField: React.FC<HipaaSignatureFieldProps> = ({
  clientId,
  isEditing,
  matterId,
  clientName,
  clientEmail,
  clientPhone,
}) => {
  // Fetch matter for firm name and contact permissions
  const { data: matter } = useGetMatterQuery(matterId ?? '', {
    skip: !matterId,
  });
  const firmName = matter?.firm?.name;
  const canContactClient = matter?.contact_client_directly !== false;
  const [showUploader, setShowUploader] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);

  // RTK Query hooks
  const {
    data: authorization,
    isLoading,
    refetch,
  } = useHipaaAuthorizationRetrieveQuery(
    { clientId },
    {
      skip: !clientId,
    },
  );

  const [createOrUpdate, { isLoading: isSaving }] =
    useHipaaAuthorizationCreateOrUpdateMutation();

  const [destroy, { isLoading: isDeleting }] =
    useHipaaAuthorizationDestroyMutation();

  // E-sign status (only if matterId is provided)
  const {
    status: eSignStatus,
    hasRequest: hasESignRequest,
    isPending: isESignPending,
    isVoided: isESignVoided,
    refreshStatus,
    isRefreshing,
    sendReminder,
    isSendingReminder,
    sendRequest: sendESignRequest,
    isSending: isSendingESign,
    voidRequest,
    isVoiding,
    canVoid,
  } = useHipaaESignStatus({
    matterId: matterId || '',
    skip: !matterId,
    refreshOnMount: true,
  });

  const hasAuthorization = authorization && authorization.signature_url;

  const handleUpload = async (file: File, signatureDate: string) => {
    try {
      await createOrUpdate({
        clientId,
        body: {
          file,
          signature_date: signatureDate,
        },
      }).unwrap();

      toast.success('HIPAA signature uploaded');
      setShowUploader(false);
      refetch();
    } catch (err) {
      toast.error('Failed to upload HIPAA signature');
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Remove HIPAA signature from file?');
    if (!confirmed) return;

    try {
      await destroy({ clientId }).unwrap();
      toast.success('HIPAA signature removed');
      refetch();
    } catch (err) {
      toast.error('Failed to remove HIPAA signature');
    }
  };

  if (isLoading) {
    return (
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-3 border-t border-gray-100">
        <dt className="text-xs font-medium text-gray-900">HIPAA Signature</dt>
        <dd className="mt-0.5 text-xs sm:col-span-2 sm:mt-0">
          <div className="animate-pulse h-4 w-24 bg-gray-200 rounded" />
        </dd>
      </div>
    );
  }

  // Use the best available signature version
  const signatureUrl =
    authorization?.cleaned_signature_url || authorization?.signature_url;

  // Format date with better labeling
  const formatSignatureDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffInDays === 0) return 'Signed today';
    if (diffInDays === 1) return 'Signed yesterday';
    if (diffInDays < 7) return `Signed ${diffInDays} days ago`;
    if (diffInDays < 30)
      return `Signed ${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365)
      return `Signed ${Math.floor(diffInDays / 30)} months ago`;

    return `Signed ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  };

  return (
    <>
      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-3 border-t-2 border-gray-200 bg-gray-50/50">
        <dt className="text-xs font-semibold text-gray-900 flex items-center gap-1">
          <FileCheck className="h-3.5 w-3.5 text-gray-600" />
          HIPAA Signature
        </dt>
        <dd className="mt-1 sm:col-span-2 sm:mt-0">
          {hasAuthorization && signatureUrl ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Interactive thumbnail */}
                <button
                  type="button"
                  onClick={() => setShowImageViewer(true)}
                  className="block flex-shrink-0 group/signature relative"
                >
                  <img
                    src={signatureUrl}
                    alt="HIPAA Signature"
                    className="h-12 w-16 object-cover rounded border-2 border-green-200 group-hover/signature:border-green-400 transition-colors"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover/signature:bg-black/10 transition-colors rounded flex items-center justify-center">
                    <Eye className="h-4 w-4 text-white opacity-0 group-hover/signature:opacity-100 transition-opacity" />
                  </div>
                </button>

                <div className="flex items-center">
                  <span className="text-xs font-medium text-green-700">
                    On file
                  </span>
                </div>
              </div>

              {/* Actions for existing signature */}
              <div className="flex items-center gap-1">
                <Button
                  onClick={() => setShowUploader(true)}
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 gap-1 text-xs"
                  disabled={isDeleting}
                >
                  <RefreshCw className="h-3 w-3" />
                  Replace
                </Button>
                {isEditing && (
                  <Button
                    onClick={handleDelete}
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 gap-1 text-xs text-red-600 hover:text-red-700"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-3 w-3" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ) : (
            // Buttons when no signature exists
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowUploader(true)}
                variant="outline"
                size="sm"
                className="h-8 px-3 gap-1.5 text-xs bg-white hover:bg-gray-50 border-gray-300"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload
              </Button>
              {matterId && !hasESignRequest && (
                <Button
                  onClick={() => setShowESignModal(true)}
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 gap-1.5 text-xs bg-white hover:bg-gray-50 border-gray-300"
                >
                  <PenLine className="h-3.5 w-3.5" />
                  Request E-Signature
                </Button>
              )}
            </div>
          )}
        </dd>
      </div>

      {/* E-Sign Status Section (when request exists) */}
      {matterId && hasESignRequest && eSignStatus && (
        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-3 border-t border-gray-100">
          <dt className="text-xs font-medium text-gray-600">E-Sign Request</dt>
          <dd className="mt-1 sm:col-span-2 sm:mt-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HipaaESignStatusBadge status={eSignStatus.status} size="sm" />
              </div>
              <div className="flex items-center gap-1">
                <Button
                  onClick={refreshStatus}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={isRefreshing}
                >
                  <ArrowPathIcon
                    className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                  />
                </Button>
                {isESignPending && (
                  <>
                    <Button
                      onClick={() => setShowReminderModal(true)}
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 gap-1 text-xs"
                    >
                      <BellIcon className="h-3.5 w-3.5" />
                      Remind
                    </Button>
                    {canVoid && (
                      <Button
                        onClick={() => setShowVoidDialog(true)}
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={isVoiding}
                      >
                        <XCircleIcon className="h-3.5 w-3.5" />
                        Void
                      </Button>
                    )}
                  </>
                )}
                {isESignVoided && (
                  <Button
                    onClick={() => setShowESignModal(true)}
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 gap-1 text-xs"
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    Resend
                  </Button>
                )}
              </div>
            </div>
          </dd>
        </div>
      )}

      {/* Upload Modal */}
      <SignatureUploadModal
        isOpen={showUploader}
        onClose={() => setShowUploader(false)}
        onUpload={handleUpload}
        onClear={handleDelete}
        isUploading={isSaving}
        existingAuthorization={authorization}
      />

      {/* Image Viewer Dialog */}
      <Dialog open={showImageViewer} onOpenChange={setShowImageViewer}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>HIPAA Authorization Signature</DialogTitle>
          </DialogHeader>
          {signatureUrl && (
            <div className="space-y-4">
              <div className="flex justify-center bg-gray-50 rounded-lg p-4">
                <img
                  src={signatureUrl}
                  alt="HIPAA Signature"
                  className="max-w-full max-h-96 object-contain"
                />
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  {authorization?.signature_date && (
                    <span>
                      {formatSignatureDate(authorization.signature_date)}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => window.open(signatureUrl, '_blank')}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    Open in New Tab
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* E-Sign Request Modal */}
      {matterId && (
        <HipaaESignRequestModal
          isOpen={showESignModal}
          onClose={() => setShowESignModal(false)}
          onSubmit={async (params) => {
            await sendESignRequest(params);
          }}
          isSubmitting={isSendingESign}
          clientName={clientName}
          clientEmail={clientEmail}
          clientPhone={clientPhone}
          firmName={firmName}
          canContactClient={canContactClient}
          zIndex={Z_INDEX_MODAL_NESTED}
        />
      )}

      {/* E-Sign Reminder Modal */}
      {matterId && (
        <HipaaReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          onSubmit={async (params) => {
            await sendReminder(params);
          }}
          isSubmitting={isSendingReminder}
          originalEmail={clientEmail}
          clientName={clientName}
          firmName={firmName}
          zIndex={Z_INDEX_MODAL_NESTED}
        />
      )}

      {/* Void Confirmation Dialog */}
      <HipaaVoidConfirmDialog
        open={showVoidDialog}
        onOpenChange={setShowVoidDialog}
        onConfirm={voidRequest}
        isVoiding={isVoiding}
        zIndex={Z_INDEX_MODAL_NESTED}
      />
    </>
  );
};
